export { default as throttle } from 'lodash.throttle';
export { default as debounce } from 'lodash.debounce';
import { useContext, useLayoutEffect, useEffect, useState } from 'react';
import * as styledComponents from 'styled-components';
import _taggedTemplateLiteral from '@babel/runtime/helpers/taggedTemplateLiteral';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import isPropValid from '@emotion/is-prop-valid';

var styled = styledComponents["default"],
  css = styledComponents.css,
  createGlobalStyle = styledComponents.createGlobalStyle,
  keyframes = styledComponents.keyframes,
  ThemeProvider = styledComponents.ThemeProvider,
  withTheme = styledComponents.withTheme,
  ServerStyleSheet = styledComponents.ServerStyleSheet,
  ThemeContext = styledComponents.ThemeContext,
  StyleSheetManager = styledComponents.StyleSheetManager;
var useTheme = function useTheme() {
  var theme = useContext(ThemeContext);
  if (!theme) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return theme;
};

var isBrowser = function isBrowser() {
  return typeof window !== 'undefined';
};
var isNode = function isNode() {
  return !isBrowser();
};

var createBreakpoint = function createBreakpoint(functionKey) {
  return function (breakpointKey) {
    return function (props) {
      return props.theme.breakpoints[functionKey](breakpointKey);
    };
  };
};
var breakpointUp = createBreakpoint('up');
var breakpoint = breakpointUp;
var breakpointDown = createBreakpoint('down');

var BASE_FONT_SIZE = 16;
var pxToRem = function pxToRem(size) {
  return size / BASE_FONT_SIZE;
};

// istanbul ignore file
var createImage = function createImage(width, height) {
  var fileFormat = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'png';
  return "https://images.ctfassets.net/3m6f3dx67i1j/3ztpsOfoWZXo4j3uVRStpF/2dbebe2dcf4ba0df5cd45b4a9b87164f/fa-image-landscape.png?w=".concat(width, "&h=").concat(height, "&fm=").concat(fileFormat, "&fit=fill");
};

var FONT_FACE_TEMPLATE = "@font-face {\n  font-family: {fontFamily};\n  font-weight: {fontWeight};\n  font-style: {fontStyle};\n  src: {sources};\n  font-display: {fontDisplay};\n}";
var FONT_URL_PREFIX_MAP = {
  dev: 'https://dev',
  local: 'https://dev',
  test: 'https://dev',
  uat: 'https://uat',
  prod: 'https://www'
};
var createFontFaceDeclaration = function createFontFaceDeclaration(face) {
  var env = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'prod';
  var urlPrefix = FONT_URL_PREFIX_MAP[env] || FONT_URL_PREFIX_MAP.prod;
  var sources = face.sources.map(function (source) {
    return "url(".concat(urlPrefix, ".").concat(source.url, ") format('").concat(source.format, "')");
  }).join(', ');
  return FONT_FACE_TEMPLATE.replace('{fontFamily}', face.fontFamily).replace('{fontWeight}', face.fontWeight.toString()).replace('{fontStyle}', face.fontStyle).replace('{sources}', sources).replace('{fontDisplay}', face.fontDisplay);
};
var createFontFaceStyles = function createFontFaceStyles(fontFaces, env) {
  if (fontFaces.length === 0) {
    return '';
  }
  return fontFaces.map(function (fontFace) {
    return createFontFaceDeclaration(fontFace, env);
  }).join('\n\n');
};

var _templateObject$2;
var aspectRatio = css(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteral(["\n  position: relative;\n  padding-bottom: ", ";\n"])), function (props) {
  return "".concat(1 / props.aspectRatio * 100, "%");
});

var _templateObject$1;
var fillAbsolute = css(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n  position: absolute;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  left: 0;\n"])));

var _templateObject;
var screenReaderOnly = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  position: absolute;\n  overflow: hidden;\n  clip: rect(0 0 0 0);\n  height: 1px;\n  width: 1px;\n  margin: -1px;\n  padding: 0;\n  border: 0;\n"])));

var useIsoLayoutEffect = isBrowser() ? useLayoutEffect : useEffect;

var reducedMotionQuery = '(prefers-reduced-motion: reduce)';
var useReducedMotion = function useReducedMotion() {
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    prefersReducedMotion = _useState2[0],
    setPrefersReducedMotion = _useState2[1];
  useEffect(function () {
    var mediaQueryList = window.matchMedia(reducedMotionQuery);

    // sets inital value to true
    setPrefersReducedMotion(mediaQueryList.matches);
    var listener = function listener(event) {
      setPrefersReducedMotion(event.matches);
    };
    mediaQueryList.addEventListener('change', listener);
    setPrefersReducedMotion(mediaQueryList.matches);
    return function () {
      mediaQueryList.removeEventListener('change', listener);
    };
  }, []);
  return prefersReducedMotion;
};

var shouldForwardProp = function shouldForwardProp(propName, target) {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
};

export { ServerStyleSheet, StyleSheetManager, ThemeProvider, aspectRatio, breakpoint, breakpointDown, breakpointUp, createFontFaceStyles, createGlobalStyle, createImage, css, fillAbsolute, isBrowser, isNode, keyframes, pxToRem, screenReaderOnly, shouldForwardProp, styled, useIsoLayoutEffect, useReducedMotion, useTheme, withTheme };
